import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { IdleService } from './idle.service';
import { StorageService } from './storage.service';
import { HttpService } from '../utils/http.service';
import { AuthEventService } from './auth-event.service';
import { DeviceSessionService } from './device-session.service';
import { HttpResponse } from '@capacitor/core';
import { GapSettings } from '../config/gapsettings';

/**
 * AuthTimingService
 * 
 * Centralized service for managing when to initialize authentication-related services
 * (IdleService) based on route navigation and user login state.
 * 
 * This service determines when to start/stop idle monitoring based on 
 * whether the user is on guest routes or authenticated routes.
 * 
 * Key behaviors:
 * - Starts when userappnav is called for the first time (user logs in)
 * - Stops when navigating to guest pages
 * - Uses simplified device-based session checking
 */
@Injectable({
  providedIn: 'root'
})
export class AuthTimingService implements OnDestroy {
  private navigationSubscription: Subscription | null = null;
  private isInitialized = false;
  private servicesInitialized = false;
  private hasTriggeredFirstTime = false; // Track if we've started timing for the first time
  private isLoggedIn = false; // Track login state
  private retryFirstAuth = false;

  constructor(
    private idleService: IdleService,
    private storage: StorageService,
    private httpService: HttpService,
    private authEventService: AuthEventService,
    private deviceSessionService: DeviceSessionService,
    private gapSettings: GapSettings,

  ) {
    this.addDebugMethods();
  }

  /**
   * Initialize the auth timing service
   * This should be called once from app.component.ts
   */
  public initialize(): void {
    if (this.gapSettings.enable_session_timeout !== '1') {
      console.log('Session timeout is disabled');
      return;
    }
    if (this.isInitialized) {
      console.log('AuthTimingService: Already initialized, skipping');
      return;
    }        
    this.isInitialized = true;
  }

  /**
   * Start auth timing for the first time - called when userappnav is first triggered
   */
  public startTimingEvents(): void {
    if (!this.gapSettings.isSessionTimeoutEnabled()) {
      console.log('Session timeout is disabled');
      return;
    }

    if (this.hasTriggeredFirstTime) {
      console.log('AuthTimingService: Auth timing already triggered for first time');
      return;
    }
    console.log('AuthTimingService: Starting auth timing for first time');
    this.hasTriggeredFirstTime = true;

    this.markAuth();
  }

  public markAuth(): void {
    if (this.gapSettings.enable_session_timeout !== '1') {
      console.log('Session timeout is disabled');
      return;
    }
    if (this.retryFirstAuth) {
      console.warn('AuthTimingService: Marking auth skipped due to retry in progress');
      return;
    }
    let retrysuccess = false;

    this.httpService.markDeviceAuthenticated().then((res) => {
      console.log('AuthTimingService: Device authenticated successfully:', res);
      if (res && res?.user_device_id) {
        this.storage.set('user_device_id', res.user_device_id);
        this.storage.set('user_session_id', res?.user_session_id ?? res.session_id);
      }
      if (res && res.success) {
        retrysuccess = true;
        this.setLoginState(true);
        // Reset device session service first time flag to enable session checking
        // wait 2 seconds before starting session checking
        setTimeout(() => {
          this.deviceSessionService.initialiazeFirstTime(); // Start device session checking
          this.checkAndInitializeServices();
        }, 2000);
      }
      // Check and initialize services
    }).catch((error) => {
      console.error('AuthTimingService: Error authenticating device:', error);
      // Handle error if needed, e.g., show notification or log
      // try again after 2 min
      if (this.retryFirstAuth) {
        return;
      }

      this.retryFirstAuth = true;
      let retryCount = 0;

      const retryInterval = setInterval(() => {

        if (retrysuccess) {
          clearInterval(retryInterval);
          this.retryFirstAuth = false;
          return;
        }
        if (retryCount >= 3) {
          clearInterval(retryInterval);
          console.error('AuthTimingService: Failed to authenticate device after 3 attempts');
          return;
        }
        this.markAuth();
        retryCount++;
      }, 120000); // Retry every 2 minutes
    });
  }

  /**
   * Check current route and initialize services if needed
   */
  private checkAndInitializeServices(): void {
    if (this.gapSettings.enable_session_timeout !== '1') {
      console.log('Session timeout is disabled');
      return;
    }
    // Only initialize if we've triggered for first time and user is logged in
    if (!this.hasTriggeredFirstTime) {
      console.log('AuthTimingService: Auth timing not triggered for first time yet, skipping');
      return;
    }
    const token = this.storage.getToken();

    if (!!token) {
      console.log('AuthTimingService: User is logged in, initializing services');
      this.initializeServices();
    } else {
      console.log('AuthTimingService: User is logged out, stopping services');
      this.cleanupServices();
    }
  }

  /**
   * Set login state and manage services accordingly
   */
  public setLoginState(isLoggedIn: boolean): void {
    if (!this.gapSettings.isSessionTimeoutEnabled()) {
      console.log('Session timeout is disabled');
      return;
    }
    console.log('AuthTimingService: Setting login state to:', isLoggedIn);
    this.isLoggedIn = isLoggedIn;
    
    if (isLoggedIn && this.hasTriggeredFirstTime ) {
      // User logged in and timing has been triggered - initialize services
      this.initializeServices();
    }
  }


  /**
   * Initialize idle service and start device session checking
   */
  private async initializeServices(): Promise<void> {
        // Check if session timeout is enabled
    if (!this.gapSettings.isSessionTimeoutEnabled()) {
      console.log('Session timeout is disabled');
      return;
    }

    // begin sevice init check
    if (this.servicesInitialized) {
      console.log('AuthTimingService: Services already initialized');
      return;
    }

    try {
      console.log('AuthTimingService: Starting idle service...');
      
      // Initialize idle service only
      await this.idleService.init();
            
      this.servicesInitialized = true;
      console.log('AuthTimingService: Services initialized successfully');
      
    } catch (error) {
      console.error('AuthTimingService: Error initializing services:', error);
    }
  }

  /**
   * Cleanup idle service and device session checking
   */
  private cleanupServices(): void {
    if (!this.servicesInitialized) {
      console.log('AuthTimingService: Services not initialized, nothing to cleanup');
      return;
    }

    console.log('AuthTimingService: Cleaning up services...');
    
    try {
      // Cleanup idle service only
      this.idleService.cleanup();
      this.idleService.reset();
      this.httpService.alreadyAuthenticated = false;
      // Stop device session checking
      this.deviceSessionService.stopChecking();    
      // Clear activity data
      this.deviceSessionService.clearActivityData();
      
      // Ensure HTTP service cleanup if needed
      this.httpService.invalidateDeviceAuth();
      
      this.deviceSessionService.firstTime = true;
      this.servicesInitialized = false;
      console.log('AuthTimingService: Services cleaned up successfully');
      
    } catch (error) {
      console.error('AuthTimingService: Error cleaning up services:', error);
    }
  }

  /**
   * Handle 401 errors from userDeviceController - redirect to login without showing network error
   */
  public handle401FromDeviceController(error: any): void {
    if (!this.gapSettings.isSessionTimeoutEnabled()) {
      console.log('Session timeout is disabled');
      return;
    }
    console.log('AuthTimingService: Handling 401 from userDeviceController, redirecting to login');
    // Emit token expired event for clean logout
    this.authEventService.emitTokenExpired('Device session expired');
  }

  /**
   * Force initialization of services (for debugging or special cases)
   */
  public forceInitializeServices(): void {
    if (!this.gapSettings.isSessionTimeoutEnabled()) {
      console.log('Session timeout is disabled');
      return;
    }
    console.log('AuthTimingService: Force initializing services...');
    this.initializeServices();
  }

  /**
   * Force cleanup of services (for debugging or special cases)
   */
  public forceCleanupServices(): void {
    console.log('AuthTimingService: Force cleaning up services...');
    this.cleanupServices();
  }

  /**
   * Get current initialization status
   */
  public getStatus(): { isInitialized: boolean; servicesInitialized: boolean } {
    return {
      isInitialized: this.isInitialized,
      servicesInitialized: this.servicesInitialized
    };
  }

  /**
   * Reset the service state
   */
  public reset(): void {
    if (!this.isInitialized) {
      console.warn('AuthTimingService: Cannot reset, service not initialized');
      return;
    }
    this.setLoginState(false);
    console.log('AuthTimingService: Resetting service state');
    this.cleanupServices();

    // Reset idle service to clear session state
    this.ngOnDestroy();
    this.isInitialized = false;
    this.hasTriggeredFirstTime = false; // Reset first time trigger
  }

  /**
   * Add debug methods to window for testing
   */
  private addDebugMethods(): void {
    // Add debug method to check current status
    (window as any).debugAuthTiming = () => {
      const status = this.getStatus();
      console.log('AuthTimingService Status:', status);
      console.log('Login state:', this.isLoggedIn);
      console.log('Has triggered first time:', this.hasTriggeredFirstTime);
      return status;
    };
    
    // Add debug method to force initialize services
    (window as any).debugForceInitServices = () => {
      console.log('DEBUG: Force initializing services...');
      this.forceInitializeServices();
    };
    
    // Add debug method to force cleanup services
    (window as any).debugForceCleanupServices = () => {
      console.log('DEBUG: Force cleaning up services...');
      this.forceCleanupServices();
    };
    
    console.log('AuthTimingService Debug methods added to window:');
    console.log('- window.debugAuthTiming()');
    console.log('- window.debugForceInitServices()');
    console.log('- window.debugForceCleanupServices()');
  }

  ngOnDestroy(): void {
    console.log('AuthTimingService: Destroying service');
    
    // Unsubscribe from navigation events
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
      this.navigationSubscription = null;
    }
    
    this.isInitialized = false;
  }
}
